/*
 * Determines whether a freelancer is eligible to apply for the Verified
 * programme. All profile-completeness criteria below must be met, the user
 * must not already be verified, must not have an existing application, and
 * their location must include an eligible country.
 *
 * See the "Verified users programme" ticket for the source-of-truth criteria.
 */

/**
 * Countries whose freelancers may apply for verification.
 * Keep in sync with App\Constants\VerifiedProgrammeCountries.
 */
export const VERIFICATION_ELIGIBLE_COUNTRIES = [
  'United States',
  'United Kingdom',
  'Canada',
  'Ireland',
  'Australia',
  // Non-EU Europe
  'Switzerland',
  'Norway',
  // EU 27
  'Austria',
  'Belgium',
  'Bulgaria',
  'Croatia',
  'Cyprus',
  'Czechia',
  'Czech Republic',
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Italy',
  'Latvia',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Netherlands',
  'Poland',
  'Portugal',
  'Romania',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
];

const hasBio = (user) => Boolean(user.bio && user.bio.length > 0);

const hasLocation = (user) =>
  Boolean(user.location && user.location.text && user.location.text.length > 0);

const hasFeaturedRoles = (user) =>
  Boolean(user.featured_roles && user.featured_roles.length > 0);

const hasSkills = (user) => Boolean(user.skills && user.skills.length > 0);

const hasWorkExperience = (user) =>
  Boolean(user.workexperience && user.workexperience.length > 0);

const hasCoverImage = (user) =>
  Boolean(user.covers && (!user.covers.placeholder || user.covers.temp));

const hasProfilePicture = (user) =>
  Boolean(user.avatars && (!user.avatars.placeholder || user.avatars.temp));

const hasUsername = (user) =>
  Boolean(user.username && !user.username.match(/^user\d+$/));

const hasPortfolioExamples = (user) =>
  Boolean(user.stats && user.stats.num_of_projects > 0);

const hasApprovedPitch = (user) =>
  Boolean(user.stats && user.stats.has_approved_pitch);

/**
 * Whether the user's location is in an eligible verification country.
 * Matches structured `location.country` or substring in `location.text`.
 *
 * @param {object} user
 * @returns {boolean}
 */
export const hasEligibleVerificationCountry = (user) => {
  if (!user || !user.location) {
    return false;
  }

  const country = (user.location.country || '').trim().toLowerCase();
  const text = (user.location.text || '').trim().toLowerCase();

  return VERIFICATION_ELIGIBLE_COUNTRIES.some((allowed) => {
    const needle = allowed.toLowerCase();
    return country === needle || (text !== '' && text.includes(needle));
  });
};

/**
 * Ordered list of completeness criteria required for verification.
 * Each entry exposes a `key`, `label` and `isComplete(user)` predicate so the
 * UI can render progress if needed.
 */
export const VERIFICATION_CRITERIA = [
  { key: 'bio', label: 'Add a bio', isComplete: hasBio },
  { key: 'location', label: 'Location', isComplete: hasLocation },
  {
    key: 'featured_roles',
    label: 'Freelance roles',
    isComplete: hasFeaturedRoles,
  },
  { key: 'skills', label: 'Specific skills', isComplete: hasSkills },
  {
    key: 'work_experience',
    label: 'Work experience',
    isComplete: hasWorkExperience,
  },
  { key: 'cover', label: 'Profile cover image', isComplete: hasCoverImage },
  { key: 'username', label: 'Username', isComplete: hasUsername },
  {
    key: 'profile_picture',
    label: 'Profile picture',
    isComplete: hasProfilePicture,
  },
  {
    key: 'portfolio',
    label: 'Portfolio examples',
    isComplete: hasPortfolioExamples,
  },
  {
    key: 'approved_pitch',
    label: 'Applied to a job',
    isComplete: hasApprovedPitch,
  },
];

/**
 * Whether the user has already submitted a verification application.
 *
 * @param {object} user
 * @returns {boolean}
 */
export const hasAppliedForVerification = (user) =>
  Boolean(user && user.verified_application);

/**
 * Whether all profile-completeness criteria for verification are met.
 *
 * @param {object} user
 * @returns {boolean}
 */
export const meetsVerificationCriteria = (user) =>
  Boolean(user) && VERIFICATION_CRITERIA.every((c) => c.isComplete(user));

/**
 * Whether the verification CTA should be offered to the user: all criteria met,
 * eligible country, not already verified, and no existing application.
 *
 * @param {object} user
 * @returns {boolean}
 */
export const isVerificationEligible = (user) =>
  Boolean(user) &&
  !user.verified &&
  !hasAppliedForVerification(user) &&
  meetsVerificationCriteria(user) &&
  hasEligibleVerificationCountry(user);

export default isVerificationEligible;
